import type { AemEnvironment, ManifestIdentifier, Manifest, ManifestScript, LoaderOptions } from '../types';
import { STAGES } from '../types/enums';

export const AssetsDomains = new Map<AemEnvironment, string>([
  ['PROD', 'https://assets.oneweb.mercedes-benz.com/plugin'],
  ['PPROD', 'https://preprod.assets.oneweb.mercedes-benz.com/plugin'],
  ['INT', 'https://int.assets.oneweb.mercedes-benz.com/plugin'],
  ['TEST', 'https://test.assets.oneweb.mercedes-benz.com/plugin'],
  ['DEV', 'https://dev.assets.oneweb.mercedes-benz.com/plugin'],
]);

const isModuleScript = (script: ManifestScript): boolean => typeof script.attrs.nomodule === 'undefined';

/**
 * Identify the environment based on the hostname.
 *
 * @param {String} url = window.location.href
 */
export function environmentResolution(url = window.location.href) {
  const { hostname } = new URL(url);

  let environment = STAGES.PROD;

  if (hostname.includes('pprod')) {
    environment = STAGES.PPROD;
  } else if (hostname.includes('int')) {
    environment = STAGES.INT;
  } else if (hostname.includes('test')) {
    environment = STAGES.TEST;
  } else if (hostname.includes('dev')) {
    environment = STAGES.DEVELOP;
  } else if (hostname.includes('localhost')) {
    environment = STAGES.INT;
  }

  return environment.toUpperCase() as AemEnvironment;
}

/**
 * Loads a manifest file from the given manifest identifier.
 */
export async function loadManifest(manifestIdentifier: ManifestIdentifier) {
  const { environment = 'PROD', version = '', name } = manifestIdentifier;
  const domain = AssetsDomains.has(environment) ? AssetsDomains.get(environment) : AssetsDomains.get('PROD');
  const path = `/${name}/manifest${version ? '-' + version : ''}.json?now=${Date.now()}`;
  const url = new URL(`${domain}${path}`);

  return fetch(url).then<Manifest>((response) => response.json());
}

/**
 * Loader function to load a script into the DOM.
 * By default it will load the script into the body.
 */
export function loader(
  url: string,
  options: LoaderOptions = {
    location: window.document.body,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    callback: () => {},
    attributes: { type: 'module', async: false, defer: false },
  },
) {
  const { attributes, callback, location } = options;
  const { async, defer, type, ...rest } = attributes || {};
  const scriptElement = document.querySelector(`script[src="${url}"]`);

  if (scriptElement !== null) {
    return callback.apply(options.callback, [location, scriptElement]);
  }

  const script = document.createElement('script');
  script.src = url;
  script.type = type;
  script.async = async;
  script.defer = defer;

  if (rest) {
    Object.keys(rest).forEach((key) => script.setAttribute(key, rest[key]));
  }

  script.onload = () => callback.apply(options.callback, [location, script]);
  script.onerror = () => callback.apply(options.callback, [new Error('Failed to load script')]);

  location.appendChild(script);
}

/**
 * Get the base path for a plugin based on the manifest file.
 */
export async function getPluginBasePath(manifestIdentifier: ManifestIdentifier) {
  const { name, environment = environmentResolution(), version = '' } = manifestIdentifier;

  const manifest = await loadManifest({ name, environment, version }).catch((error) => {
    throw new Error(`Failed to load manifest: plugin \n ${error.message}`);
  });

  return `${AssetsDomains.get(environment)}/${name}/${name}-${manifest.version}`;
}

/**
 * Register a plugin by loading the manifest file and loading the scripts into the DOM.
 */
export async function registerPlugin(
  manifestIdentifier: ManifestIdentifier,
  location: HTMLElement = window.document.body,
) {
  const manifest = await loadManifest(manifestIdentifier).catch((error) => {
    throw new Error(`Failed to load manifest: ${manifestIdentifier.name} \n ${error.message}`);
  });

  const { scripts } = manifest.config.files;

  const metaScript = scripts.filter(isModuleScript).map((script) => {
    return {
      url: `${AssetsDomains.get(
        manifestIdentifier?.environment ?? (STAGES.PROD.toUpperCase() as unknown as AemEnvironment),
      )}${script.url}`,
      options: {
        attributes: { type: 'module', async: false, defer: false, ...script.attrs },
      },
    };
  });

  return Promise.all(
    metaScript.map((meta) => {
      return new Promise((resolve, reject) => {
        loader(meta.url, {
          ...meta.options,
          location,
          callback: function (location) {
            location instanceof Error ? reject(location) : resolve(location);
          },
        });
      });
    }),
  );
}

/**
 * Import a plugin by loading the manifest file from asset server.
 */
export async function importPlugin<Resource = unknown>(manifestIdentifier: ManifestIdentifier): Promise<Resource> {
  const { name, environment = environmentResolution(), version = '' } = manifestIdentifier;
  const { importModule = async (name: string) => await import(name) } = window || {};

  const manifest = await loadManifest({ name, environment, version }).catch((error) => {
    throw new Error(`Failed to load manifest: ${name} \n ${error.message}`);
  });

  const { scripts } = manifest.config.files;

  const [moduleScript] = scripts.filter(isModuleScript).map((script) => {
    return `${AssetsDomains.get(environment)}${script.url}`;
  });

  return importModule(moduleScript);
}
